import { z } from 'zod';
import { IsoDateTime, Uuid, withDefaults } from './common.js';
/**
 * Module `wallet` (docs/modules/wallet.md, spec 8.6.8). Owner: S49. Google Wallet first; Apple Wallet is Phase 7.
 * The pass's QR code is the same signed token the kiosk already accepts (pre-registration QR or member QR), never an id.
 */
export const WalletSubjectType = z.enum(['preregistration', 'member']);
export const WalletPassState = z.enum(['active', 'expired', 'voided']);
export const WalletPass = z.object({
    id: Uuid,
    subjectType: WalletSubjectType,
    subjectId: Uuid,
    state: WalletPassState,
    /** Bumped on each update sent to Google. */
    version: z.number().int(),
    savedAt: IsoDateTime.nullable(),
    updatedAt: IsoDateTime,
    lastSyncErrorKey: z.string().nullable(),
});
/** "Add to Google Wallet" link (a signed JWT save URL). Made on demand; never stored. */
export const WalletSaveLink = z.object({ url: z.string(), provider: z.enum(['google', 'log']) });
export const WalletTenantSettings = z.object({
    forVisitors: z.boolean().default(true),
    forMembers: z.boolean().default(true),
    /** Card title; empty = the tenant's display name. */
    cardTitle: z.string().trim().max(60).default(''),
});
/** Platform (Oakford): the Google Wallet issuer. 'log' records what would be sent; 'off' hides the buttons everywhere. */
export const WalletPlatformSettings = z.object({
    google: withDefaults(z.object({
        provider: z.enum(['off', 'log', 'google']).default('log'),
        issuerId: z.string().max(40).default(''),
        serviceAccountEmail: z.string().max(200).default(''),
        /** PEM private key of the service account, encrypted with the server key; write-only in the portal. */
        privateKeyEnc: z.string().max(8000).default(''),
    })),
});
/** Platform admin: set the Google Wallet issuer. The private key is write-only; it is encrypted before it is stored. */
export const WalletCredentialsUpdate = z.object({
    provider: z.enum(['off', 'log', 'google']).optional(),
    issuerId: z.string().trim().max(40).optional(),
    serviceAccountEmail: z.string().trim().max(200).optional(),
    /** PEM private key of the service account. Never returned. */
    privateKey: z.string().trim().min(40).max(6000).optional(),
});
export const WalletPlatformStatus = z.object({
    provider: z.enum(['off', 'log', 'google']),
    issuerId: z.string(),
    serviceAccountEmail: z.string(),
    keySet: z.boolean(),
    /** True when passes are really sent to Google (provider google and all three details set). */
    live: z.boolean(),
});
/** Google's save callback (ECv2SigningOnly envelope). */
export const WalletCallbackBody = z.object({
    signature: z.string().max(2000),
    intermediateSigningKey: z.object({ signedKey: z.string().max(4000), signatures: z.array(z.string().max(2000)).max(10) }),
    protocolVersion: z.string().max(40),
    signedMessage: z.string().max(4000),
});
//# sourceMappingURL=wallet.js.map